import React, { useEffect, useRef } from 'react';
import { useParticipants, useLocalParticipant } from '@livekit/components-react';
import { VideoTile } from './VideoTile';
import { CustomLayoutManagerProps } from './types';

// Custom Layout Manager Component - Must be inside LiveKit room context
export const CustomLayoutManager: React.FC<CustomLayoutManagerProps> = ({ 
    currentLayout, 
    userRole, 
    teacherParticipantId, 
    pinnedParticipant, 
    onParticipantPin, 
    onTeacherIdentified, 
    onScreenShareChange,
    isMirrored = false
}) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    
    // Track last screen share state to avoid redundant callbacks
    const lastScreenShareRef = useRef<{ participantId: string | null }>({ participantId: null });

    // Identify teacher when participants change - completely rewritten
    // Log component state changes
    useEffect(() => {
        console.log('📊 Layout Manager State:', {
            currentLayout,
            participantCount: participants.length,
            hasLocalParticipant: !!localParticipant,
            teacherFound: !!teacherParticipantId,
            pinnedParticipant,
            screenShareActive: participants.some(p => {
                const tracks = [...p.trackPublications.values()] as any[];
                return tracks.some(pub => 
                    pub.source === 'screen_share' || 
                    pub.source === 'screen' ||
                    (pub.kind === 'video' && pub.source !== 'camera')
                );
            })
        });
    }, [currentLayout, participants, localParticipant, teacherParticipantId, pinnedParticipant]);

    useEffect(() => {
        console.log('🔍 Teacher identification check:', {
            userRole,
            localParticipant: !!localParticipant,
            localParticipantId: localParticipant?.identity,
            teacherParticipantId,
            onTeacherIdentified: !!onTeacherIdentified,
            participants: participants.map(p => ({
                id: p.identity,
                isTeacher: p.identity === teacherParticipantId,
                metadata: p.metadata,
                tracks: [...p.trackPublications.values()].map((pub: any) => ({
                    kind: pub.kind,
                    source: pub.source
                }))
            }))
        });
        
        // Identify teacher when they join
        if (userRole === 'teacher' && localParticipant && onTeacherIdentified) {
            console.log('👨‍🏫 Identifying teacher:', localParticipant.identity);
            onTeacherIdentified(localParticipant.identity);
        }

        // Log all participants and their tracks for debugging
        console.log('👥 Current participants:', participants.map(p => ({
            id: p.identity,
            isTeacher: p.identity === teacherParticipantId,
            isLocal: p.identity === localParticipant?.identity,
            tracks: [...p.trackPublications.values()].map((pub: any) => ({
                kind: pub.kind,
                source: pub.source,
                isMuted: pub.isMuted
            }))
        })));
    }, [participants, localParticipant, userRole, onTeacherIdentified, teacherParticipantId]);

    // Detect screen sharing - completely rewritten
    useEffect(() => {
        const checkScreenShare = () => {
            // Find who is screen sharing - check multiple ways
            const screenSharingParticipant = participants.find(p => {
                // Check trackPublications for screen share tracks
                const tracks = [...p.trackPublications.values()] as any[];
                const hasScreenShareTrack = tracks.some(pub => {
                    // Only consider as screen share if source is explicitly 'screen_share' or 'screen'
                    const isExplicitScreenShare = pub.source === 'screen_share' || pub.source === 'screen';
                    // Also must have an active track
                    const hasActiveTrack = pub.track && pub.track.mediaStream?.active;
                    return isExplicitScreenShare && hasActiveTrack;
                });
                
                // Don't check videoTrackPublications separately - trackPublications already covers it
                return hasScreenShareTrack;
            });
            
            console.log('🖥️ Screen share check (strict mode):', {
                screenSharingParticipant: screenSharingParticipant?.identity,
                localParticipantId: localParticipant?.identity,
                allParticipants: participants.map(p => {
                    const tracks = [...p.trackPublications.values()] as any[];
                    const screenShareTracks = tracks.filter(pub => 
                        (pub.source === 'screen_share' || pub.source === 'screen') && 
                        pub.track && pub.track.mediaStream?.active
                    );
                    return {
                        id: p.identity,
                        hasScreenShare: screenShareTracks.length > 0,
                        screenShareTracks: screenShareTracks.map((pub: any) => ({
                            source: pub.source,
                            kind: pub.kind,
                            trackState: pub.track?.state,
                            streamActive: pub.track?.mediaStream?.active
                        }))
                    };
                })
            });
            
            // Track last state to avoid redundant callbacks
            const currentScreenShareId = screenSharingParticipant?.identity || null;
            const lastScreenShareId = lastScreenShareRef.current.participantId;
            
            if (currentScreenShareId !== lastScreenShareId) {
                console.log('🔄 Screen share state changed:', {
                    from: lastScreenShareId,
                    to: currentScreenShareId
                });
                
                if (onScreenShareChange) {
                    if (screenSharingParticipant) {
                        // Someone started screen sharing
                        onScreenShareChange(true, screenSharingParticipant.identity);
                    } else {
                        // Screen sharing stopped
                        onScreenShareChange(false);
                    }
                }
                
                lastScreenShareRef.current.participantId = currentScreenShareId;
            } else {
                console.log('⏭️ Screen share state unchanged, skipping callback');
            }
        };

        checkScreenShare();
        
        // Check periodically for screen share changes
        const interval = setInterval(checkScreenShare, 1000);
        return () => clearInterval(interval);
    }, [participants, localParticipant, onScreenShareChange]);

    // Ensure VideoConference is always visible - it contains the video tracks
    // Custom layouts overlay on top, but VideoConference must remain visible
    // DISABLED: This was causing flickering/black overlay issues
    // VideoConference is now handled via CSS in LiveKitRoom.tsx
    // useEffect(() => {
    //     const videoConference = document.querySelector('.lk-video-conference');
    //     if (videoConference) {
    //         // Always keep VideoConference visible - it provides video tracks
    //         (videoConference as HTMLElement).style.display = 'block';
    //         (videoConference as HTMLElement).style.visibility = 'visible';
    //         (videoConference as HTMLElement).style.opacity = '1';
    //         console.log('✅ Ensuring VideoConference is visible for video tracks');
    //     }
    // }, [currentLayout]); // Re-check when layout changes

    // Re-enable custom layouts with fixes
    console.log('🎥 CustomLayoutManager - Current layout:', currentLayout, 'User role:', userRole, 'Local participant:', localParticipant?.identity);
    console.log('🎥 Participants count:', participants.length, 'Local participant exists:', !!localParticipant);
    
    // Error boundary for layout rendering
    try {
        // Always show at least the local participant
        if (!localParticipant) {
            console.log('🎥 No local participant found, showing default VideoConference');
            const videoConference = document.querySelector('.lk-video-conference');
            if (videoConference) {
                (videoConference as HTMLElement).style.display = 'block';
                (videoConference as HTMLElement).style.visibility = 'visible';
                (videoConference as HTMLElement).style.opacity = '1';
            }
            return null;
        }

        if (currentLayout === 'screen-share') {
            return <ScreenShareLayout 
                participants={participants}
                localParticipant={localParticipant}
                teacherParticipantId={teacherParticipantId}
                onParticipantPin={onParticipantPin}
                pinnedParticipant={pinnedParticipant}
                isMirrored={isMirrored}
            />;
        }

        if (userRole === 'teacher') {
            return <TeacherLayout 
                participants={participants}
                localParticipant={localParticipant}
                teacherParticipantId={teacherParticipantId}
                onParticipantPin={onParticipantPin}
                pinnedParticipant={pinnedParticipant}
                isMirrored={isMirrored}
            />;
        } else {
            return <StudentLayout 
                participants={participants}
                localParticipant={localParticipant}
                teacherParticipantId={teacherParticipantId}
                onParticipantPin={onParticipantPin}
                pinnedParticipant={pinnedParticipant}
                isMirrored={isMirrored}
            />;
        }
    } catch (error) {
        console.error('❌ Layout rendering error:', error);
        // Ensure VideoConference is visible on error
        const videoConference = document.querySelector('.lk-video-conference');
        if (videoConference) {
            (videoConference as HTMLElement).style.display = 'block';
            (videoConference as HTMLElement).style.visibility = 'visible';
            (videoConference as HTMLElement).style.opacity = '1';
        }
        return null;
    }
};

// Teacher Layout - Full screen with horizontal row
const TeacherLayout: React.FC<{
    participants: any[];
    localParticipant: any;
    teacherParticipantId: string | null;
    onParticipantPin: (participantId: string) => void;
    pinnedParticipant: string | null;
    isMirrored?: boolean;
}> = ({ participants, localParticipant, teacherParticipantId, onParticipantPin, pinnedParticipant, isMirrored = false }) => {
    // Simple grid layout for teacher view
    // Get only students (filter out teacher/local participant)
    const students = participants.filter(p => p.identity !== localParticipant?.identity);

    // Log teacher status
    console.log('👨‍🏫 Teacher status:', {
        teacherId: teacherParticipantId,
        isTeacher: localParticipant?.identity === teacherParticipantId,
        studentCount: students.length
    });

    return (
        <div className="teacher-layout">
            <div className="video-grid">
                {/* Teacher in main area */}
                {localParticipant && (
                    <div className="video-cell teacher-cell">
                        <VideoTile
                            participant={localParticipant}
                            isLocal={true}
                            isTeacher={true}
                            isPinned={pinnedParticipant === localParticipant.identity}
                            isActiveSpeaker={localParticipant.isSpeaking}
                            onClick={() => onParticipantPin(localParticipant.identity)}
                            className="grid-video"
                            isMirrored={isMirrored}
                        />
                    </div>
                )}

                {/* Only students in grid */}
                {students.map((participant) => (
                    <div key={participant.identity} className="video-cell">
                        <VideoTile
                            participant={participant}
                            isLocal={false}
                            isTeacher={false}
                            isPinned={pinnedParticipant === participant.identity}
                            isActiveSpeaker={participant.isSpeaking}
                            onClick={() => onParticipantPin(participant.identity)}
                            className="grid-video"
                            isMirrored={false}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

// Student Layout - Focus view with teacher as main presenter
const StudentLayout: React.FC<{
    participants: any[];
    localParticipant: any;
    teacherParticipantId: string | null;
    onParticipantPin: (participantId: string) => void;
    onTeacherIdentified?: (teacherId: string) => void;
    pinnedParticipant: string | null;
    isMirrored?: boolean;
}> = ({ participants, localParticipant, teacherParticipantId, onParticipantPin, onTeacherIdentified, pinnedParticipant, isMirrored = false }) => {
    // Find teacher and other participants - handle both identified and unidentified teacher
    const teacher = teacherParticipantId 
        ? participants.find(p => p.identity === teacherParticipantId)
        : participants.find(p => p.metadata?.includes('"role":"teacher"')); // Check metadata for teacher role

    // Update teacher ID if found through metadata
    if (teacher && !teacherParticipantId && teacher.identity !== teacherParticipantId) {
        console.log('👨‍🏫 Found teacher through metadata:', teacher.identity);
        if (onTeacherIdentified) {
            onTeacherIdentified(teacher.identity);
        }
    }

    const otherStudents = participants.filter(p => 
        p.identity !== (teacherParticipantId || teacher?.identity) && 
        p.identity !== localParticipant?.identity
    );

    console.log('🎓 StudentLayout participants:', {
        teacherId: teacherParticipantId,
        teacherFound: !!teacher,
        teacherIdentity: teacher?.identity,
        localParticipantId: localParticipant?.identity,
        otherStudents: otherStudents.map(p => p.identity),
        allParticipants: participants.map(p => ({
            id: p.identity,
            metadata: p.metadata,
            isTeacher: p.identity === teacherParticipantId || p.metadata?.includes('"role":"teacher"'),
            tracks: [...p.trackPublications.values()].map((pub: any) => ({
                kind: pub.kind,
                source: pub.source,
                isMuted: pub.isMuted
            }))
        }))
    });

    console.log('🎓 StudentLayout - Teacher ID:', teacherParticipantId);
    console.log('🎓 StudentLayout - Teacher found:', !!teacher);
    console.log('🎓 StudentLayout - Participants:', participants.length);
    console.log('🎓 StudentLayout - Local participant:', !!localParticipant);
    console.log('🎓 StudentLayout - All participants:', participants.map(p => ({
        id: p.identity,
        isTeacher: p.identity === teacherParticipantId
    })));

    // Simple grid layout for student view
    return (
        <div className="student-layout">
            {/* Main grid area */}
            <div className="video-grid">
                {/* Teacher always first if present */}
                {teacher && (
                    <div className="video-cell teacher-cell">
                        <VideoTile
                            participant={teacher}
                            isLocal={false}
                            isTeacher={true}
                            isPinned={pinnedParticipant === teacher.identity}
                            isActiveSpeaker={teacher.isSpeaking}
                            onClick={() => onParticipantPin(teacher.identity)}
                            className="grid-video"
                            isMirrored={false}
                        />
                    </div>
                )}

                {/* Local participant (student) */}
                {localParticipant && (
                    <div className="video-cell student-cell">
                        <VideoTile
                            participant={localParticipant}
                            isLocal={true}
                            isTeacher={false}
                            isPinned={pinnedParticipant === localParticipant.identity}
                            isActiveSpeaker={localParticipant.isSpeaking}
                            onClick={() => onParticipantPin(localParticipant.identity)}
                            className="grid-video"
                            isMirrored={isMirrored}
                        />
                    </div>
                )}

                {/* Other students */}
                {otherStudents.map((student) => (
                    <div key={student.identity} className="video-cell">
                        <VideoTile
                            participant={student}
                            isLocal={false}
                            isTeacher={false}
                            isPinned={pinnedParticipant === student.identity}
                            isActiveSpeaker={student.isSpeaking}
                            onClick={() => onParticipantPin(student.identity)}
                            className="grid-video"
                            isMirrored={false}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};

// Screen Share Layout - completely rewritten
const ScreenShareLayout: React.FC<{
    participants: any[];
    localParticipant: any;
    teacherParticipantId: string | null;
    onParticipantPin: (participantId: string) => void;
    pinnedParticipant: string | null;
    isMirrored?: boolean;
}> = ({ participants, localParticipant, teacherParticipantId, onParticipantPin, pinnedParticipant, isMirrored = false }) => {
            // Find who is screen sharing - only explicit screen share sources
            const screenSharingParticipant = participants.find(p => {
                const tracks = [...p.trackPublications.values()] as any[];
                const screenShareTrack = tracks.find(pub => {
                    // Only consider as screen share if source is explicitly 'screen_share' or 'screen'
                    const isExplicitScreenShare = pub.source === 'screen_share' || pub.source === 'screen';
                    // Also must have an active track
                    const hasActiveTrack = pub.track && pub.track.mediaStream?.active;
                    return isExplicitScreenShare && hasActiveTrack;
                });
                
                if (screenShareTrack) {
                    console.log('🖥️ Found screen share track:', {
                        participant: p.identity,
                        track: {
                            source: screenShareTrack.source,
                            kind: screenShareTrack.kind,
                            isMuted: screenShareTrack.isMuted,
                            trackState: screenShareTrack.track?.state,
                            streamActive: screenShareTrack.track?.mediaStream?.active
                        }
                    });
                    return true;
                }
                return false;
            });

    // Get all participants including local
    const allParticipants = localParticipant 
        ? [localParticipant, ...participants.filter(p => p.identity !== localParticipant.identity)]
        : participants;

    console.log('🖥️ ScreenShareLayout:', {
        screenSharer: screenSharingParticipant?.identity,
        participantCount: allParticipants.length,
        allParticipants: allParticipants.map(p => ({
            id: p.identity,
            isTeacher: p.identity === teacherParticipantId,
            tracks: [...p.trackPublications.values()].map((pub: any) => ({
                source: pub.source,
                kind: pub.kind
            }))
        }))
    });

    return (
        <div className="screen-share-layout">
            {/* Two-column layout */}
            <div className="screen-content">
                {/* Screen share */}
                <div className="screen-area">
                    {screenSharingParticipant ? (
                        <VideoTile
                            participant={screenSharingParticipant}
                            isLocal={screenSharingParticipant.identity === localParticipant?.identity}
                            isTeacher={screenSharingParticipant.identity === teacherParticipantId}
                            isPinned={true}
                            isActiveSpeaker={false}
                            onClick={() => {}}
                            className="screen-share-video"
                            isMirrored={false}
                        />
                    ) : (
                        <div className="screen-placeholder">
                            <p>No screen share active</p>
                        </div>
                    )}
                </div>

                {/* Participants grid */}
                <div className="participants-grid">
                    {allParticipants.map((participant) => (
                        <div key={participant.identity} className="video-cell">
                            <VideoTile
                                participant={participant}
                                isLocal={participant.identity === localParticipant?.identity}
                                isTeacher={participant.identity === teacherParticipantId}
                                isPinned={pinnedParticipant === participant.identity}
                                isActiveSpeaker={participant.isSpeaking}
                                onClick={() => onParticipantPin(participant.identity)}
                                className="grid-video"
                                isMirrored={participant.identity === localParticipant?.identity && isMirrored}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
